import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WheelSVG } from "./WheelSVG";
import { playClickSound } from "../game/audio";

// Ambient floating "+1" numbers that continuously appear around the wheel.
// They spawn on a timer at random positions within a ring around the wheel
// and float upward, mimicking the mockup's ambient sparkles.
const useAmbientFloaters = (enabled = true) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled) return () => {};
    let mounted = true;
    const spawn = () => {
      if (!mounted) return;
      const id = Date.now() + Math.random();
      const angle = Math.random() * Math.PI * 2;
      const radius = 42 + Math.random() * 8; // percentage from center
      const x = 50 + Math.cos(angle) * radius;
      const y = 50 + Math.sin(angle) * radius * 0.9;
      setItems((prev) => [...prev, { id, x, y }].slice(-14));
      setTimeout(() => {
        setItems((prev) => prev.filter((f) => f.id !== id));
      }, 1800);
    };
    const t = setInterval(spawn, 550);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [enabled]);
  return items;
};

// Continuous sparkles (small yellow dots) drifting outward around the wheel.
const useAmbientSparkles = (count = 24) => {
  const sparkles = React.useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        angle: (i / count) * Math.PI * 2 + Math.random() * 0.5,
        radius: 44 + Math.random() * 10,
        size: 1.5 + Math.random() * 2,
        delay: Math.random() * 3,
        duration: 2 + Math.random() * 2.5,
      })),
    [count],
  );
  return sparkles;
};

export const CentralWheel = ({ eraKey, perClick, onClick, soundEnabled }) => {
  const [rotation, setRotation] = useState(0);
  const [floaters, setFloaters] = useState([]);
  const [particles, setParticles] = useState([]);
  const [pressed, setPressed] = useState(false);
  const rotRef = useRef(0);

  // Fewer ambient effects on mobile for perf + cleanliness.
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
  const sparkleCount = isMobile ? 12 : 24;

  const ambient = useAmbientFloaters(true);
  const sparkles = useAmbientSparkles(sparkleCount);

  const handleClick = useCallback(
    (e) => {
      onClick();
      if (soundEnabled) playClickSound(eraKey);

      const delta = 40 + Math.random() * 40;
      rotRef.current += delta;
      setRotation(rotRef.current);

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const id = Date.now() + Math.random();
      setFloaters((prev) => [...prev, { id, x, y, value: perClick }]);
      setTimeout(() => {
        setFloaters((prev) => prev.filter((f) => f.id !== id));
      }, 900);

      const parts = Array.from({ length: 14 }).map((_, i) => ({
        id: id + i,
        x,
        y,
        dx: (Math.random() - 0.5) * 220,
        dy: (Math.random() - 0.5) * 220 - 40,
        size: 2 + Math.random() * 3,
      }));
      setParticles((prev) => [...prev, ...parts]);
      setTimeout(() => {
        setParticles((prev) => prev.filter((p) => !parts.find((pp) => pp.id === p.id)));
      }, 800);

      setPressed(true);
      setTimeout(() => setPressed(false), 120);
    },
    [eraKey, onClick, perClick, soundEnabled],
  );

  // Continuous idle rotation
  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      rotRef.current += dt * 6;
      setRotation(rotRef.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="relative w-full flex items-center justify-center select-none">
      {/* Outer glow ring */}
      <div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        style={{
          width: "min(84vw, 620px)",
          height: "min(84vw, 620px)",
          background:
            "radial-gradient(circle, rgba(255,212,0,0.22) 0%, rgba(255,212,0,0.08) 40%, rgba(255,212,0,0) 70%)",
        }}
      />

      <button
        type="button"
        onClick={handleClick}
        data-testid="wheel-button"
        aria-label="Rueda principal — clic para fabricar"
        className="relative outline-none focus-visible:ring-2 focus-visible:ring-[#FFD400] rounded-full active:scale-95 transition-transform"
        style={{ width: "min(80vw, 520px)", height: "min(80vw, 520px)" }}
      >
        {/* Ambient sparkles */}
        {sparkles.map((s) => {
          const cx = 50 + Math.cos(s.angle) * s.radius;
          const cy = 50 + Math.sin(s.angle) * s.radius;
          return (
            <motion.span
              key={s.id}
              aria-hidden
              className="absolute rounded-full pointer-events-none"
              style={{
                left: `${cx}%`,
                top: `${cy}%`,
                width: s.size,
                height: s.size,
                backgroundColor: "#FFD400",
                boxShadow: "0 0 6px #FFD400",
                translate: "-50% -50%",
              }}
              animate={{
                opacity: [0.15, 0.9, 0.15],
                scale: [0.7, 1.3, 0.7],
              }}
              transition={{
                duration: s.duration,
                delay: s.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}

        {/* Ambient floating +1 texts */}
        <AnimatePresence>
          {ambient.map((f) => (
            <motion.span
              key={f.id}
              initial={{ opacity: 0, y: 0, scale: 0.7 }}
              animate={{ opacity: [0, 0.9, 0], y: -40, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.8, ease: "easeOut" }}
              className="absolute pointer-events-none font-black text-sm sm:text-base"
              style={{
                left: `${f.x}%`,
                top: `${f.y}%`,
                color: "#FFD400",
                textShadow: "0 0 6px rgba(255,212,0,0.6)",
                fontFamily: "Orbitron, sans-serif",
                translate: "-50% -50%",
              }}
            >
              +1
            </motion.span>
          ))}
        </AnimatePresence>

        <motion.div
          animate={{
            rotate: rotation,
            scale: pressed ? 0.94 : 1,
          }}
          transition={{
            rotate: { type: "spring", stiffness: 60, damping: 18 },
            scale: { type: "spring", stiffness: 700, damping: 22 },
          }}
          className="w-full h-full relative z-10"
        >
          <WheelSVG eraKey={eraKey} />
        </motion.div>

        {/* Click-triggered particles */}
        <AnimatePresence>
          {particles.map((p) => (
            <motion.span
              key={p.id}
              initial={{ x: p.x, y: p.y, opacity: 1, scale: 1 }}
              animate={{ x: p.x + p.dx, y: p.y + p.dy, opacity: 0, scale: 0.3 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute top-0 left-0 rounded-full pointer-events-none"
              style={{
                width: p.size,
                height: p.size,
                backgroundColor: "#FFD400",
                boxShadow: "0 0 8px #FFD400",
              }}
            />
          ))}
        </AnimatePresence>

        {/* Click-triggered floating +N Berry text */}
        <AnimatePresence>
          {floaters.map((f) => (
            <motion.span
              key={f.id}
              initial={{ x: f.x, y: f.y, opacity: 0 }}
              animate={{ x: f.x, y: f.y - 90, opacity: [0, 1, 1, 0] }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="absolute top-0 left-0 pointer-events-none font-black text-lg sm:text-xl"
              style={{
                color: "#FFD400",
                textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                fontFamily: "Orbitron, sans-serif",
                transform: "translate(-50%, -50%)",
              }}
              data-testid="floating-berry-text"
            >
              +{f.value.toLocaleString("es-ES")} Berry
            </motion.span>
          ))}
        </AnimatePresence>
      </button>
    </div>
  );
};

export default CentralWheel;
